import { requireImportTarget } from "./import-target";
import { createShapeId, toRichText } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { BoardDocument, BoardTable } from "../shared/schema";
import { uploadFile } from "./api";
import { imagePlacement } from "./sheet-operations";
import { importedRichText } from "./rich-import";
import {
  checkedRows,
  MAX_CONTENT_BYTES,
  contentBytes,
} from "../shared/file-content";
import { documentHeightFor } from "./ContentShapes";
export type ImportMode = "file" | "document" | "table" | "grid" | "drawing";
export async function importEditable(
  editor: Editor,
  room: string,
  file: File,
  signal: AbortSignal,
  cloud: boolean,
  mode: "document" | "table" | "grid",
  headers = true,
  sheet = 1,
  placement = imagePlacement(editor),
  shapeId: import("@tldraw/tlschema").TLShapeId = createShapeId(),
) {
  if (!Number.isInteger(sheet) || sheet < 1 || sheet > 16)
    throw new Error("Choose a worksheet from 1 to 16.");
  const { extract } = await import("./document-previews");
  const data = await extract(file, signal, true, sheet);
  const richText =
    mode === "document"
      ? data.html
        ? importedRichText(data.html)
        : data.text
          ? toRichText(data.text)
          : null
      : null;
  const rows = mode !== "document" && data.rows ? checkedRows(data.rows) : null;
  if (!richText && !rows)
    throw new Error(
      "Editable conversion is unavailable for this file. Choose a file preview.",
    );
  if (contentBytes(richText || rows) > MAX_CONTENT_BYTES)
    throw new Error("Converted content is too large. Choose a file preview.");
  if (
    richText &&
    documentHeightFor(
      {
        props: {
          w: 560,
          h: 720,
          name: file.name,
          source: "",
          richText,
          fontSize: 18,
        },
      },
      editor,
    ) > 30000
  )
    throw new Error(
      "This document would be clipped on the board. Choose a reference preview or split the source document.",
    );
  const source = await uploadFile(room, file, signal, cloud);
  signal.throwIfAborted();
  if (editor.getIsReadonly()) throw new Error("Edit access was removed.");
  const parent = requireImportTarget(editor, placement);
  if (editor.getShape(shapeId)) return shapeId;
  const id = shapeId;
  editor.markHistoryStoppingPoint("Convert document");
  if (richText)
    editor.createShape<BoardDocument>({
      opacity: 1,
      id,
      type: "document",
      parentId: parent.parentId,
      x: parent.center.x - 280,
      y: parent.center.y - 160,
      props: { name: file.name.slice(0, 120), source: source.src, richText },
    });
  else {
    const w = Math.max(400, Math.min(1600, rows![0].length * 160));
    editor.createShape<BoardTable>({
      opacity: 1,
      id,
      type: "table",
      parentId: parent.parentId,
      x: parent.center.x - w / 2,
      y: parent.center.y - 100,
      props: {
        name: file.name.slice(0, 120),
        source: source.src,
        rows: rows!,
        headers,
        layout: mode === "grid" ? "grid" : "table",
        w,
        h: 44 + rows!.length * (mode === "grid" ? 72 : 38),
      },
    });
  }
  editor.select(id);
  return id;
}
